import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { FileCheck, FileText } from "lucide-react"

import { logger } from "@repo/utils/logger"
import { getProjectDocumentsQueryOptions } from "~/modules/projects/project-documents.queries"
import { getExtendedProjectQueryOptions } from "~/modules/projects/projects.queries"
import { DocumentLink } from "~/modules/storage/components/document-link"

const log = logger.child({
  module: "projects-project-documents-route",
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/projects/$projectId/documents",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    context.queryClient
      .ensureQueryData(
        getExtendedProjectQueryOptions({ projectId: params.projectId }),
      )
      .catch((error) => {
        log.error("Error getting extended project", { error })
        return null
      })
  },
})

function RouteComponent() {
  const params = Route.useParams()

  const { data: project } = useSuspenseQuery(
    getExtendedProjectQueryOptions({ projectId: params.projectId }),
  )

  const { data: documents } = useSuspenseQuery(
    getProjectDocumentsQueryOptions({ projectId: project?.id || "" }),
  )

  if (!project) {
    return <div>Project not found</div>
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-white/70">No documents found</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {documents.map((doc) => (
        <DocumentLink
          key={doc.id}
          document={doc}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-lg bg-white/5 p-3 transition-colors hover:bg-white/10"
        >
          <FileText className="h-6 w-6 text-white/70" />
          <div className="flex-grow">
            <p className="font-medium">
              {(doc.metadata as { original_filename?: string })
                ?.original_filename || "Document"}
            </p>
            <p className="text-white/70 text-xs capitalize">{doc.doc_type}</p>
          </div>
          <FileCheck className="h-5 w-5 text-green-500" />
        </DocumentLink>
      ))}
    </div>
  )
}
